<template>
    <section class="section section-shaped section-lg my-0">
        <div class="shape shape-style-1 bg-gradient-default">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
        </div>
        <div class="container pt-lg-md">
            <div class="row justify-content-center">
              <!-- Content Start -->
              <div class="col-lg-8 col-md-10 mx-auto">
                <h1 class="post-heading">Private equity in Japan: a perfect storm ?
</h1>	

                <span class="meta"> Posted in September 2022 </span>

                <figure>
                  <img class="img-fluid" src="img/blog/card/privateequityjapan_bw.png" alt="">
                </figure>
                
<p>I’m a VC guy not a PE guy, so when I start opining about private equity, readers should grant my words a tepid reception. Yet I am observing a phenomenon here on the ground in Japan that I thought might be relevant to share.</p>



<p>Let's start with recapping the current macroeconomic backdrop, a context upon which numerous experts -- both armchair and real -- have weighed in. Massive runaway inflation has taken root in most developed economies. At last print, CPI, a core measure of inflation in the U.S., ticked up to 8.6%.</p>



<p>Governments and particularly central banks -- whose core mandate is to keep inflation under control -- have found themselves behind the curve. As a result, the U.S. Federal Reserve Bank, followed not far behind by the European Central Bank and the Bank of England, have shifted to a steady diet of interest rate hikes and quantitative tightening, sending asset prices plummeting, with seemingly no asset class immune (equities, real estate, crypto assets, you name it).</p>




<p>The one glaring exception to all this within the G7 countries is Japan. In Japan, depending on how broad a basket you take, CPI inflation has risen to only 2.5%, and if stripping out food and energy from the calculation, inflation in Japan currently sits at a mere 0.3%, 20x lower than the comparable measure in the U.S.</p>



<p>Accordingly, the bank of Japan has maintained its policy of yield curve control, effectively capping yields on 10-year government bonds to 25 basis points. The impact of course of this stark disparity, i.e. with other countries hiking rates and tightening while Japan maintains low rates, has manifested itself in a drastic JPY devaluation to a 20-year low, as I’ve written about before.</p>



<p>In light of the Yen's tumble, there has been some speculation in the markets that the BOJ will relent on its yield curve control policy in order to bolster its currency. However, consensus here in Tokyo seems to be that as long as inflation in Japan does not get out of hand, it's unlikely that the BOJ would do anything else but stay the course. Furthermore, BOJ governor Kuroda-san's final mandate ends next spring. The likelihood of him implementing a radical policy shift in the final nine months of his mandate appears low.</p>



<p>So this brings me back to the topic of private equity.  When executed successfully, private equity transactions can generate value creation in up to three different ways (VCs like to joke that there are only three, but I’ll resist the temptation here): </p>



<p>1.	Operational efficiencies</p>



<p>2.	Multiple expansion</p>



<p>3.	Leverage</p>



<p>Operational efficiencies can result from restructuring. Divestment of underperforming assets, unlocking cost savings, bolt-on acquisitions, realignment of management incentives, are among other expertise that PE firms can bring to a company once they take control.</p>



<p>Multiple expansion means positioning a company to justify higher EV/S and EV/EBITDA multiples (enterprise value/sales, enterprise value/EBITDA, respectively). Higher multiples can be attained via both internal actions such as enhanced strategic focus, improved corporate governance, and external factors such as investing in a sector which is growing or coming back into favor.</p>



<p>Leverage means using a significant portion of debt to acquire the target company in the PE buyout. A typical leveraged buyout of a company for say $100 million might entail $30 million of equity from the PE fund and $70 million of debt from lenders.</p>



<p>As you can imagine, combining two or all three above factors can exponentially enhance the financial return profile of the investment. Let's say that the aforementioned $100 million company is valued at a multiple of 5x EBITDA, (EBITDA = 100m / 5 => 20m). The transaction is financed with 30m from the PE fund and 70m in outside debt. If the PE firm through operational efficiencies is able to increase EBITDA from 20m to 30m,  and in parallel is able to justify that the company thanks to its improved strategic focus and sectorial growth justifies an EV/EBITDA multiple of 7 rather than 5, the enterprise value of the company becomes $210 million. If the PE fund can find a buyer for the company at this price, it will generate a return on its invested capital of 4.67x ((210m - 70m debt)/30m).</p>



<p>When viewing Japan through the lens of the above three factors for private equity value creation, the market here looks pretty attractive.</p>



<p>Without naming names, it’s no secret that many incumbent corporations carry underperforming business lines on their books, and hence offer some opportunities ripe  for restructuring, which in turn could unlock operational efficiencies. Additionally, Japan’s new ESG compliance requirements are forcing some companies to restructure and in certain cases even carve out business units.</p>



<p>Regarding the principle of multiple expansion, EV/EBITDA multiples are moving in quite the opposite direction worldwide, as rising rates depress asset prices. Yet I would submit that such forces of multiple compression run deeper in the U.S. and Europe right now than what we are witnessing in Japan.</p>



<p>However, thanks to its low interest rate environment, debt financing in Japan remains a relative bargain compared to the rest of the world. The opportunity to structure buyout transactions with inexpensive leverage is where Japan really shines on these vectors for private equity value creation.</p>




<p>Moreover, the perception in Japan of the business of private equity, even of foreign funds, has been gradually improving. In the eyes of foreign PE funds, the Japanese market represents a reliable beacon of security and rule of law.</p>



<p>Upon admittedly superficial analysis, it stands to reason that Japan should represent an appealing market for global PE funds in the current environment.</p>



<p>We’re already witnessing some evidence of movement. At the start of the latest annual shareholding meeting season, a record 77 companies faced proposals from stock owners, many of them foreign funds. In March, Sweden's EQT acquired Bering Private Equity Asia, with stated expansion plans for Japan. The potential imminent $20 billion buyout of Toshiba would serve as a bellwether.</p>



<p>Whether these data points portend a broader trend remains to be seen, but if they do, this could result in increased competition for Japan's domestic PE firms. (Unlike venture deals, in which VC firms often invest collaboratively as syndicates, private equity is more of a solo sport). An informal survey suggests to me that they are not alarmed.</p>



<p>Perhaps I'm straying too far out of my lane here, but because I enjoy these hypothetical thought experiments, here's my unsolicited (and probably unwelcome) advice to Japan's domestic PE firms: build relationships upstream, i.e. with venture capital funds in Japan. The market here still remains quite opaque to foreigners at the venture stage, so you have an inherent competitive advantage by being on the ground. Granted, not all venture companies grow into private equity targets, but high-growth firms in some sectors often do, such as in enterprise SaaS, or alternatively can serve as complementary targets for PE build-up strategies. Building such relationships today will lay the groundwork for future dealflow before the competitive bidding process even begins.</p>


              </div>
            </div>
        </div>
    </section>
</template>
<script>
export default {};
</script>
<style>
.post-heading,
.subheading,
.section-heading,
.blockquote,
.meta,
p,
span {
  color: #fff;
}
.post-heading {
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}
.subheading {
  font-size: 20px;
  margin-top: 16px;
}
.section-heading {
  font-weight: 700;
  margin-top: 32px;
}
.section-heading + p {
  margin-top: 16px;
}
.meta + p {
  margin-top: 32px;
}
.author-info {
  font-size: 14px;
  font-style: italic;
  line-height: 1.5;
}
figure {
  margin-top: 24px;
}
</style>

